import { useNavigate, useParams } from "react-router-dom";
import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import type { CreateVenue, UpdateVenue } from "~pages/Venues/types/upsertVenue.ts";
import { useExportVenues } from "~pages/Venues/VenueListPage/hooks/useExportVenues.ts";
import { useImportVenues } from "~pages/Venues/VenueListPage/hooks/useImportVenues.ts";
import type { DefaultOperationSuccessResponse } from "~types/response.ts";
import { isDefaultOperationSucceeded } from "~utils/defaultOperationResponse.ts";
import { useCreateVenue } from "./useCreateVenue.ts";
import { useDeleteVenue } from "./useDeleteVenue.ts";
import { useUpdateVenue } from "./useUpdateVenue.ts";

export const useVenueService = () => {
  const { mutateAsync: createVenueFn, isPending: isCreatePending } = useCreateVenue();
  const { mutateAsync: updateVenueFn, isPending: isUpdatePending } = useUpdateVenue();
  const { mutateAsync: deleteVenueFn, isPending: isDeletePending } = useDeleteVenue();
  const { mutateAsync: importVenuesFn, isPending: isImportPending } = useImportVenues();
  const { mutateAsync: exportVenuesFn, isPending: isExportPending } = useExportVenues();
  const { customerId, sport, organizationId } = useParams();
  const navigate = useNavigate();

  const { create, update, remove, executeOrThrow } = useApiOperations(ApiOperationItems.VENUE);

  const createVenue = async (payload: CreateVenue, options?: { redirectToList?: boolean }) => {
    const createdVenue = await create(() => createVenueFn(payload));

    if (createdVenue && options?.redirectToList) {
      navigate(`/${customerId}/admin/${sport}/${organizationId}/venues`, { state: { search: payload.nameLocal } });
    }

    return createdVenue;
  };

  const updateVenue = (venueId: string, payload: Omit<UpdateVenue, "venueId">) => update(() => updateVenueFn({ ...payload, venueId }));

  const deleteVenue = async (venueId: string) => Boolean(await remove(() => deleteVenueFn(venueId)));

  const importVenues = async (fileName: string) =>
    executeOrThrow(() => importVenuesFn(fileName), {
      successMessageId: "venues.import.started",
      isSucceeded: isDefaultOperationSucceeded,
    });

  const exportVenues = async (fieldNames: Array<string>): Promise<DefaultOperationSuccessResponse> =>
    executeOrThrow(() => exportVenuesFn(fieldNames), {
      successMessageId: "venues.export.started",
      isSucceeded: isDefaultOperationSucceeded,
    });

  return {
    createVenue,
    isCreatePending,
    updateVenue,
    isUpdatePending,
    deleteVenue,
    isDeletePending,
    importVenues,
    isImportPending,
    exportVenues,
    isExportPending,
  };
};
